import * as React from 'react';
import * as trace from 'owa-trace';
import getCurrentSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import LoadMoreError from 'mini-error-handler/lib/components/LoadMoreError';
import loadMoreInTable from 'mini-mail-list-actions/lib/actions/tables/loadMoreInTable';
import MailItemWrapper from './MailItemWrapper';
import MailListHeader from '../MailListHeader';
import MailListLoading from '../MailListLoading';
import OfflineSyncInProgressBar from './OfflineSyncInProgressBar';
import styles from './VirtualizedMailListItem.scss';
import UpNextCard from 'mini-mail-list-upnext-view/lib/components/UpNextCard';
import { getOwaMiniCommonStrings } from 'owa-strings/owa-mini-common-strings';
import { LazyAutomaticRepliesBanner } from 'mini-mail-automatic-replies-banner-view';
import { LazyFocusedInboxRollup } from 'mini-mail-focused-inbox-rollup-view';
import { LazyOnboardingCarouselElement } from 'mini-mail-list-onboarding-carousel';
import { LazyUSQWarningCard } from 'mini-mail-list-storage-warning-card';
import { NativeAdContainer } from 'mini-mail-nativead-view';
import type { NativeAdPlacement } from 'mini-mail-nativead-store';
import { RowComponentsID } from '../../utils/mailList/getListItemHeight';
import type { ItemRendererProp } from 'mini-virtualized-list/lib/schema/VirtualizedListProps';
import { checkObjectDeepEquality } from 'mini-common-utils/lib/utils/checkObjectDeepEquality';

const VirtualizedMailListItem = React.memo(
    (props: ItemRendererProp<string>) => {
        const { data, index, style, forwardedRefs } = props;
        const virtualListItemId = data[index].split(' ')[0]; // The index of Ad is appended in the item ID to show multiple Ads
        const adIndex = data[index].split(' ')[1] as NativeAdPlacement; // The ID contains space separated Ad index in case of AdContainerExp

        if (!virtualListItemId) {
            trace.errorThatWillCauseAlert('VirtualizedMailListItem: VirtualListItemId not found');
            return null;
        }

        let listItemElem: JSX.Element = null;
        let className: string = '';

        switch (virtualListItemId) {
            case RowComponentsID.ListHeader:
                listItemElem = <MailListHeader forwardedRefs={forwardedRefs} />;
                className = styles.mailListHeader;
                break;
            case RowComponentsID.AutomaticReplyBanner:
                listItemElem = <LazyAutomaticRepliesBanner />;
                break;
            case RowComponentsID.FocusedInboxRollup:
                listItemElem = <LazyFocusedInboxRollup />;
                break;
            case RowComponentsID.AdBannerContainer:
                listItemElem = <NativeAdContainer adIndex={adIndex} />;
                className = styles.adBannerContainer;
                break;
            case RowComponentsID.AdContainerExp:
                listItemElem = <NativeAdContainer adIndex={adIndex} />;
                break;
            case RowComponentsID.Spinner:
                listItemElem = <MailListLoading className={styles.mailListLoading} />;
                break;
            case RowComponentsID.LoadError:
                listItemElem = (
                    <LoadMoreError
                        title={getOwaMiniCommonStrings().mailListLoadMoreErrorTitle}
                        onRetry={() => loadMoreInTable(getCurrentSelectedTableView())}
                        spacerOptions={{ bottomSpacer: true }}
                    />
                );
                break;
            case RowComponentsID.UpNext:
                return <UpNextCard style={style} />;

            case RowComponentsID.USQ:
                listItemElem = <LazyUSQWarningCard />;
                break;

            case RowComponentsID.OnboardingCardsCarousel:
                listItemElem = <LazyOnboardingCarouselElement />;
                break;
            case RowComponentsID.OfflineSyncInProgress:
                listItemElem = <OfflineSyncInProgressBar />;
                break;
            default:
                return (
                    <MailItemWrapper
                        itemId={virtualListItemId}
                        isScrolling={props.isScrolling}
                        style={style}
                    />
                );
        }

        return (
            <div style={style} className={className}>
                {listItemElem}
            </div>
        );
    },
    (prevProps, nextProps) => {
        return checkObjectDeepEquality(prevProps, nextProps);
    }
);

export default VirtualizedMailListItem;
